import ItineraryGroup from '~/models/ItineraryGroup'
import { ItinerarySpot2 } from '~/models/ItinerarySpot'
import Media from '~/models/Media'
import { Page } from '~/models/PaginatedItems'
import { Place } from '~/models/Place'
import { Spot2 } from '~/models/Spot'
import { ItineraryService } from '~/passporter-services/itinerary/service'
import { PlaceService } from '~/passporter-services/place/service'
import { SpotService } from '~/passporter-services/spot/service'
import EventsService from '~/services/events'
import { TranslationService } from '~/services/Translation'
import { useItineraryStore } from '~/store/itinerary'
import { usePlaceStore } from '~/store/place'

export class TripSpotActions {
	private static get itineraryStore() {
		return useItineraryStore()
	}
	private static get placeStore() {
		return usePlaceStore()
	}

	static async getSpots(itineraryId: string, page: number, groupId?: string): Promise<Page<Spot2>> {
		try {
			const stored = this.itineraryStore.retrieveSpots(itineraryId, groupId)
			const isStored: boolean = stored && page <= stored.currentPage
			if (isStored) {
				return {
					items: stored.spots,
					count: stored.total,
					page: stored.currentPage,
				}
			}
			const spots = await ItineraryService.getSpots(itineraryId, page, groupId)
			this.itineraryStore.addSpotsTo(itineraryId, page, spots, groupId)
			return spots
		} catch (e) {
			console.error(e)
			return {} as Page<Spot2>
		}
	}

	static async getSpot(spotId: string): Promise<Spot2> {
		try {
			return await SpotService.getSpot(spotId)
		} catch (e) {
			console.error(e)
			return {} as Spot2
		}
	}

	static async addSpot(
		itineraryId: string,
		place: Spot2 | Place,
		transformPlaceToSpot: boolean
	): Promise<Spot2[]> {
		const translation = TranslationService.getInstance()
		let spots: Spot2[] = []
		try {
			let spotId: string = place.id!
			if (transformPlaceToSpot) {
				const spotFromPlace: Spot2 = await SpotService.createSpotFromPlace(place.id!)
				spotId = spotFromPlace.id!
			}
			spots = await ItineraryService.addSpot(itineraryId, spotId)
			const translate = translation.translate('myPlaces_successFeedback_placeSaved')
			EventsService.emitSuccess(translate)
		} catch {
			EventsService.emitError(translation.translate('myPlaces_errorFeedback_placeNotSaved'))
		}
		return spots
	}

	static async addSpots(
		itineraryId: string,
		spots: Spot2[],
		collectionId?: string
	): Promise<Spot2[]> {
		const spotIds = spots.map((spot) => spot.id!)
		return await ItineraryService.addSpots(itineraryId, spotIds, collectionId)
	}

	static async addSpotsByIds(itineraryId: string, spotIds: string[]): Promise<Spot2[]> {
		return await ItineraryService.addSpots(itineraryId, spotIds)
	}

	static async deleteSpot(itineraryId: string, spotId: string, spotName: string): Promise<void> {
		const translation = TranslationService.getInstance()
		try {
			await ItineraryService.deleteSpot(itineraryId, spotId)
			EventsService.emitSuccess(translation.translate('myPlaces_successFeedback_placeRemoved'))
		} catch {
			EventsService.emitError(
				translation.translateWithParams('web.itinerary.toast.place_remove_error', {
					placeName: spotName,
				})
			)
		}
	}

	static async updateSpot(
		itineraryId: string,
		spotId: string,
		group: ItineraryGroup
	): Promise<Spot2 | undefined> {
		const translation = TranslationService.getInstance()

		try {
			const previousSpotResponse = (await ItineraryService.updateSpot(
				itineraryId,
				spotId,
				group.id!
			)) as ItinerarySpot2

			previousSpotResponse.groups = [group]
			EventsService.emitSuccess(translation.translate('panel_toast_save_changes'))
			return previousSpotResponse
		} catch {
			EventsService.emitError(translation.translate('errorFeedback_notSaved'))
		}
	}

	static async deleteGroupFromSpot(
		itineraryId: string,
		spotId: string
	): Promise<Spot2 | undefined> {
		const translation = TranslationService.getInstance()
		try {
			const previousSpotResponse = (await ItineraryService.deleteGroupFromSpot(
				itineraryId,
				spotId
			)) as ItinerarySpot2
			previousSpotResponse.groups = []
			EventsService.emitSuccess(translation.translate('panel_toast_save_changes'))
			return previousSpotResponse
		} catch {
			EventsService.emitError(translation.translate('errorFeedback_notSaved'))
		}
	}

	static async resetSpots(
		itineraryId: string,
		page: number,
		groupId?: string
	): Promise<Page<Spot2>> {
		try {
			const spots = await ItineraryService.getSpots(itineraryId, page, groupId)
			this.itineraryStore.resetSpots(itineraryId)
			this.itineraryStore.addSpotsTo(itineraryId, page, spots, groupId)
			return spots
		} catch (e) {
			console.error(e)
			return {} as Page<Spot2>
		}
	}

	static async getSpotMedias(
		spotId: string,
		page: number = 0,
		pageSize: number = 4
	): Promise<Page<Media>> {
		try {
			const stored: Media[] | undefined = this.itineraryStore.retrieveSpotMedias(spotId, page)
			if (stored) {
				return {
					items: stored,
				}
			}
			const spotMedias = await SpotService.getMedias(spotId, page, pageSize)
			this.itineraryStore.addSpotMediasTo(spotId, page, spotMedias.items)
			return spotMedias
		} catch (e) {
			console.error(e)
			return {} as Page<Media>
		}
	}

	static async getPlace(id: string): Promise<Place | undefined> {
		try {
			const stored: Place | undefined = this.placeStore.getPlace(id)
			if (stored) return stored
			const place = await PlaceService.getPlaceBy(id)
			if (place) this.placeStore.setPlace(place)
			return place
		} catch {
			const translation = TranslationService.getInstance()
			EventsService.emitError(translation.translate('Common_error'))
			return undefined
		}
	}
}
